import { Component } from '@theme/component';

/**
 * Manages the header-custom drawer's level-1 → level-2 → level-3
 * drill-down.
 *
 * Clicking a level-1 link with children hides that group's own level-1
 * list and reveals its children in the same spot (sibling groups are never
 * touched), while a single global breadcrumb (back arrow + title) appears
 * at the top of the drawer and never changes label past this point,
 * regardless of how much deeper the merchant drills.
 *
 * Clicking a level-2 link that itself has children reveals a level-3
 * column/list (desktop: a side column with a vertical rule; below the
 * drawer's own modal breakpoint: the level-2 panel collapses into a
 * horizontal tab strip and level 3 takes the main vertical space instead),
 * without touching the level-1 drill or any other group.
 *
 * The back button is depth-aware: from level 3 it steps back to level 2
 * only; only from level 2 does it fully reset to level 1. This is why
 * there are two reset methods — a narrow one (level 3 only) and a full one
 * (everything).
 *
 * A `drawer-menu-media` block's image panel can match either a level-1 or
 * a level-2 title (by exact title text, case-insensitively) — whichever is
 * the deepest currently active drill wins, recomputed from scratch on
 * every transition so level-1's own media correctly reappears the moment
 * level 3 closes, without needing to track "which depth caused it"
 * separately.
 *
 * Level-3 panels can only be generated in Liquid from within their
 * originating group's own nested loop (blocks/_drawer-menu-group.liquid),
 * but visually belong to one column shared across every group — so they're
 * tagged `data-menu-portal="level3"` and physically relocated into that
 * shared column here, before refs are scanned. Same "nest for data access,
 * relocate for layout" technique already used in this theme by
 * assets/timeless-favorites.js.
 *
 * Level-4 links are always real `<a>` tags; this component never drills
 * past level 3.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} backButton - The back-arrow button.
 * @property {HTMLAnchorElement} breadcrumbTitle - The breadcrumb's title link.
 * @property {HTMLElement} navDivider - The divider shown under the breadcrumb once drilled.
 * @property {HTMLElement} level3Column - The shared level-3 column that panels are relocated into.
 * @property {HTMLElement[]} [level1] - Each group's own level-1 list.
 * @property {HTMLElement[]} [level2] - Each expandable level-1 link's children panel (`data-menu-title`-keyed).
 * @property {HTMLElement[]} [level3] - Each expandable level-2 link's children panel (`data-menu-title`-keyed).
 * @property {HTMLElement[]} [mediaPanel] - Each configured media panel (`data-menu-title`-keyed).
 *
 * @extends {Component<Refs>}
 */
class HeaderCustomDrawerNav extends Component {
  requiredRefs = ['backButton', 'breadcrumbTitle', 'navDivider', 'level3Column'];

  /** @type {Element | null} */
  #drawer = null;

  // Keep each repeated Brands/Shapes panel attached to its own trigger,
  // even after moving the panels into the shared third column.
  #submenus = new WeakMap();

  #alignmentObserver = new ResizeObserver(() => this.#alignSubmenus());

  // Measure the shared menu start, never the clicked parent item's position.
  #alignSubmenus() {
    if (!this.classList.contains('is-level3') || !window.matchMedia('(min-width: 990px)').matches) return;
    const groups = this.querySelector('.header-custom-drawer__groups');
    if (!groups) return;
    const offset = Math.max(0, groups.getBoundingClientRect().top - this.getBoundingClientRect().top);
    this.style.setProperty('--drawer-submenu-top', `${offset}px`);
  }

  connectedCallback() {
    this.#relocate();
    super.connectedCallback();

    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener('theme-drawer:close', this.#reset);
    for (const element of this.querySelectorAll('.header-custom-drawer__top, .header-custom-drawer__nav-primary')) {
      this.#alignmentObserver.observe(element);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawer?.removeEventListener('theme-drawer:close', this.#reset);
    this.#alignmentObserver.disconnect();
  }

  /**
   * Move each level-3 panel out of the group it was generated in and into
   * the single shared level-3 column. Must run before refs are scanned,
   * since the column ref only resolves correctly once every panel that
   * belongs in it is actually there.
   */
  #relocate() {
    const column = this.querySelector('.header-custom-drawer__level3');
    if (!column) return;

    this.querySelectorAll('[data-menu-portal="level3"]').forEach((node) => {
      const trigger = node.previousElementSibling;
      if (trigger?.matches('.header-custom-drawer__link--child')) this.#submenus.set(trigger, node);
      column.appendChild(node);
    });
  }

  /**
   * Drill into the clicked link's children — level 1 or level 2, detected
   * by the trigger's own ancestry (level-2 triggers live inside a
   * .header-custom-drawer__level2 panel, level-1 triggers don't).
   * @param {Event} event
   */
  drillInto = (event) => {
    const trigger = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-menu-title]') : null
    );
    if (!trigger) return;

    if (trigger.closest('.header-custom-drawer__level2')) {
      this.#drillLevel2(trigger);
    } else {
      this.#drillLevel1(trigger);
    }
  };

  /**
   * Return one level: from level 3 to level 2, or from level 2 to level 1.
   * The breadcrumb label never changes, so this is the only way to tell
   * how far back a single click should go.
   */
  back = () => {
    if (this.classList.contains('is-level3')) {
      this.#resetLevel3();
      this.#syncMedia();
    } else {
      this.#reset();
    }
  };

  /**
   * @param {HTMLElement} trigger
   */
  #drillLevel1(trigger) {
    this.#reset();

    const title = trigger.dataset.menuTitle ?? '';
    const key = title.toLowerCase();

    trigger.closest('.header-custom-drawer__level1')?.setAttribute('hidden', '');

    const group = trigger.closest('.header-custom-drawer__group');
    const level2 = (this.refs.level2 ?? []).find((panel) => group?.contains(panel) && panel.dataset.menuTitle === key);
    if (level2) level2.removeAttribute('hidden');

    this.refs.breadcrumbTitle.textContent = title;
    this.refs.breadcrumbTitle.setAttribute('href', trigger.dataset.menuUrl || '#');
    this.refs.backButton.parentElement?.removeAttribute('hidden');
    this.refs.navDivider.removeAttribute('hidden');
    this.classList.add('is-drilled');

    this.#syncMedia();
  }

  /**
   * @param {HTMLElement} trigger
   */
  #drillLevel2(trigger) {
    this.#resetLevel3();

    const level3 = this.#submenus.get(trigger);
    if (!level3) return;

    trigger.classList.add('is-active');
    level3.removeAttribute('hidden');
    this.refs.level3Column.removeAttribute('hidden');
    this.classList.add('is-level3');

    this.#syncMedia();
    this.#alignSubmenus();
  }

  /**
   * Show whichever drawer-menu-media panel matches the deepest currently
   * active title — the active level-2 trigger's title if level 3 is open,
   * otherwise the breadcrumb's (level-1) title. Always recomputed from
   * scratch rather than tracked incrementally, so switching between
   * sibling level-2/level-3 items — or closing level 3 back to level 2 —
   * always shows exactly the one matching panel, never a stale one.
   */
  #syncMedia() {
    (this.refs.mediaPanel ?? []).forEach((panel) => panel.setAttribute('hidden', ''));
    this.classList.remove('is-expanded');

    const activeLevel2Trigger = this.querySelector('.header-custom-drawer__link--child.is-active');
    const key = activeLevel2Trigger
      ? (activeLevel2Trigger.dataset.menuTitle ?? '').toLowerCase()
      : (this.refs.breadcrumbTitle.textContent ?? '').toLowerCase();
    if (!key) return;

    const mediaPanel = (this.refs.mediaPanel ?? []).find((panel) => panel.dataset.menuTitle === key);
    if (mediaPanel) {
      const menuPanel = activeLevel2Trigger
        ? this.#submenus.get(activeLevel2Trigger)
        : (this.refs.level2 ?? []).find((panel) => !panel.hasAttribute('hidden'));
      const links = menuPanel ? [...menuPanel.querySelectorAll('a[href]')] : [];
      // Alphabetical brand groups keep their links in portaled panels.
      menuPanel?.querySelectorAll('.header-custom-drawer__link--child').forEach((trigger) => {
        links.push(...(this.#submenus.get(trigger)?.querySelectorAll('a[href]') ?? []));
      });
      const normalize = (text) => (text ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
      let visibleItems = 0;
      mediaPanel.querySelectorAll('.header-custom-drawer__media-item').forEach((item) => {
        const overlay = item.querySelector('a.header-custom-drawer__media-link');
        const match = links.find((link) => normalize(link.textContent) === normalize(overlay?.textContent));
        const href = match?.getAttribute('href');
        // A promotional label must never fall back to an unrelated collection.
        item.hidden = !overlay || !href || href === '#';
        if (!item.hidden) {
          overlay.setAttribute('href', href);
          visibleItems++;
        }
      });
      if (!visibleItems) return;
      mediaPanel.removeAttribute('hidden');
      this.classList.add('is-expanded');
    }
  }

  /**
   * Narrow reset: collapses level 3 only. Level 1's own drilled state (and
   * therefore the level-2 panel currently shown as its children/tabs)
   * stays exactly as it was.
   */
  #resetLevel3() {
    (this.refs.level3 ?? []).forEach((panel) => panel.setAttribute('hidden', ''));
    this.refs.level3Column.setAttribute('hidden', '');
    this.querySelectorAll('.header-custom-drawer__link--child.is-active').forEach((trigger) => {
      trigger.classList.remove('is-active');
    });
    this.classList.remove('is-level3');
  }

  /**
   * Full reset back to the level-1 view — also the drawer's own
   * theme-drawer:close handler, so reopening the drawer always starts
   * fresh instead of resuming whatever depth it was left at.
   */
  #reset = () => {
    this.#resetLevel3();

    (this.refs.level1 ?? []).forEach((panel) => panel.removeAttribute('hidden'));
    (this.refs.level2 ?? []).forEach((panel) => panel.setAttribute('hidden', ''));
    (this.refs.mediaPanel ?? []).forEach((panel) => panel.setAttribute('hidden', ''));

    this.refs.breadcrumbTitle.textContent = '';
    this.refs.backButton.parentElement?.setAttribute('hidden', '');
    this.refs.navDivider.setAttribute('hidden', '');
    this.classList.remove('is-drilled', 'is-expanded');
  };
}

if (!customElements.get('header-custom-drawer-nav')) {
  customElements.define('header-custom-drawer-nav', HeaderCustomDrawerNav);
}
